import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { useOtaConfig } from './useQueries';
import { runOtaCheck } from '../lib/ota';

/**
 * Drives OTA live updates on the native app. Mounted once near the app root, it:
 *   - runs a check on launch and whenever the live `config/ota` manifest changes
 *     (so publishing a release / flipping the kill switch reaches open apps), and
 *   - re-checks on resume (a release shipped while the app was backgrounded is
 *     picked up without a cold start).
 * No-op on web (OTA is native-only); the actual download/apply + rollback safety
 * lives in `lib/ota` and the Capgo plugin.
 */
export function useOtaUpdater(): void {
  const { data: cfg } = useOtaConfig();
  // Keep the latest manifest available to the resume listener without rebinding it.
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    void runOtaCheck(cfg);
  }, [cfg]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let remove: (() => void) | undefined;
    const sub = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void runOtaCheck(cfgRef.current);
    });
    void sub.then((handle) => {
      remove = () => handle.remove();
    });
    return () => remove?.();
  }, []);
}
